import React, { useState, useRef, useEffect, Fragment } from 'react';
import { addTotalToMatchingSharedBrand } from '../../elements/utils';

export type TrendValue = {
  raw: number | null;
  formatted: string;
  secondaryFormatted?: string;
};

export type TrendRow = {
  label: string;
  values: TrendValue[];
  isTotals?: boolean;
};

export type TrendsInfo = {
  rows: TrendRow[];
  periods: string[];
};

type Props = {
  info: TrendsInfo;
  sharedBrands?: string[];
};
function KpiTrendChart({ info, sharedBrands }: Readonly<Props>) {
  const [index, setIndex] = useState<number | undefined>();
  const { rows, periods } = info;

  const handleMouseOver = (index: number) => {
    setIndex(index);
  };

  return (
    <div className="relative" onMouseLeave={() => setIndex(undefined)}>
      {rows.map((row, idx) => (
        <Row key={idx} row={row} index={index} onMouseOver={handleMouseOver} sharedBrands={sharedBrands} />
      ))}
      <Periods periods={periods} index={index} />
    </div>
  );
}

type RowProps = {
  row: TrendRow;
  onMouseOver: (index: number) => void;
  index?: number;
  sharedBrands: string[];
};
function Row({ row, index, onMouseOver, sharedBrands }: Readonly<RowProps>) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const { label, values, isTotals = false } = row;

  useEffect(() => {
    if (!canvas.current) {
      return;
    }

    const canvasEl = canvas.current;
    const animationFrameId = requestAnimationFrame(() => {
      const canvasRect = canvasEl.getBoundingClientRect();
      if (canvasRect.width === 0 || canvasRect.height === 0) {
        return; // Don't proceed if dimensions are 0
      }
      // rest of the drawing code...
      canvasEl.width = canvasRect.width;
      canvasEl.height = canvasRect.height;

      const maxValue = Math.max(...values.map(v => v.raw));
      const tickLength = canvasEl.width / (values.length - 1);
      const ticks: (number | null)[] = [];
      values.forEach(value => {
        if (value === null) {
          ticks.push(null);
        } else {
          const tickHeight = (value.raw / maxValue) * canvasEl.height;
          ticks.push(tickHeight);
        }
      });

      const maxTick = Math.max(...ticks);
      const minTick = Math.min(...ticks);
      const offset = (canvasEl.height - (maxTick - minTick)) / 2;

      const ctx = canvasEl.getContext('2d');
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = isTotals ? '#879BCC' : '#4368BD';
      ctx.fillStyle = isTotals ? '#DBE0EB' : '#AFBBDB';

      ctx.translate(0.5, 0.5); // half the line width to straddle the pixels
      ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
      ctx.beginPath();

      const line = new Path2D();
      const area = new Path2D();
      area.moveTo(0, canvasEl.height);

      let x = 0;
      let y = 0;
      ticks.forEach((tick, idx) => {
        if (idx === 0) {
          x = 0;
          y = canvasEl.height - tick + offset;
          line.moveTo(x, y);
          area.lineTo(x, y);
        } else {
          x = idx * tickLength;
          y = canvasEl.height - tick + offset;
          line.lineTo(x, y);
          area.lineTo(x, y);
        }
      });

      area.lineTo(x, canvasEl.height);
      area.closePath();

      ctx.fill(area);
      ctx.stroke(line);

      ctx.translate(-0.5, -0.5); // reset
    });
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [values, isTotals]);

  const displayValue = index !== undefined;

  const overlaysArray = Array(row.values.length).fill(null);
  const formattedLabel = addTotalToMatchingSharedBrand(label, label, sharedBrands);
  return (
    <div className="relative flex flex-row items-stretch h-16 border-b border-light">
      <div className="w-1/4 flex-shrink-0 flex flex-column justify-end leading-tight">
        {!displayValue && <span className="block nowrap overflow-ellipses">{formattedLabel}</span>}
        {displayValue && <span className="block text-xs text-lighter nowrap overflow-ellipses">{formattedLabel}</span>}
        {displayValue && (
          <span className="block">
            {values[index]?.formatted || '-'}{' '}
            <span className="text-sm text-lighter">{values[index]?.secondaryFormatted || ''}</span>
          </span>
        )}
      </div>
      <div className="flex-grow relative pt-2">
        <canvas ref={canvas} className="block z-1 relative h-full w-full" />
        <div className="absolute inset-0 flex flex-row">
          {overlaysArray.map((_, idx) => (
            <Fragment key={`${row.label}_${idx}`}>
              {idx === 0 && (
                <div
                  onMouseEnter={() => onMouseOver(idx)}
                  className={`flex-1 h-full border-l border-transparent ${index === idx ? 'border-brand' : ''}`}
                />
              )}
              {idx === overlaysArray.length - 1 && (
                <div
                  onMouseEnter={() => onMouseOver(idx)}
                  className={`flex-1 h-full border-r border-transparent ${index === idx ? 'border-brand' : ''}`}
                />
              )}
              {idx > 0 && idx < overlaysArray.length - 1 && (
                <>
                  <div
                    onMouseEnter={() => onMouseOver(idx)}
                    className={`flex-1 h-full border-r border-transparent ${index === idx ? 'border-brand' : ''}`}
                  />
                  <div onMouseEnter={() => onMouseOver(idx)} className={`flex-1 h-full`} />
                </>
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

type PeriodProps = {
  periods: string[];
  index?: number;
};
function Periods({ periods, index }: PeriodProps) {
  const overlaysArray = Array(periods.length).fill(null);

  return (
    <div className="relative flex flex-row">
      <div className="w-1/4 flex-shrink-0">&nbsp;</div>
      <div className="relative flex-grow">
        {index === undefined && (
          <div className="flex flex-row justify-between text-lighter">
            <span>{periods[0]}</span>
            <span>{periods[periods.length - 1]}</span>
          </div>
        )}
        {index !== undefined && (
          <div className="absolute inset-0 flex flex-row">
            {overlaysArray.map((_, idx) => (
              <Fragment key={`pov_${idx}`}>
                {idx === 0 && (
                  <div
                    className={`flex-1 relative overflow-visible h-full border-l border-transparent ${
                      index === idx ? 'border-brand' : ''
                    }`}
                  >
                    {index === idx && <div className="absolute nowrap text-brand left-0 pl-1">{periods[idx]}</div>}
                  </div>
                )}
                {idx === overlaysArray.length - 1 && (
                  <div
                    className={`flex-1 relative overflow-visible h-full border-r border-transparent ${
                      index === idx ? 'border-brand' : ''
                    }`}
                  >
                    {index === overlaysArray.length - 1 && (
                      <div className="absolute nowrap text-brand right-0 pr-1">{periods[idx]}</div>
                    )}
                  </div>
                )}
                {idx > 0 && idx < overlaysArray.length - 1 && (
                  <>
                    <div
                      className={`flex-1 relative h-full border-r border-transparent ${
                        index === idx ? 'border-brand' : ''
                      }`}
                    >
                      {index == idx && (
                        <div
                          className={`absolute nowrap text-brand ${
                            idx <= overlaysArray.length / 2 ? 'left-full pl-1' : 'right-0 pr-1'
                          }`}
                        >
                          {periods[idx]}
                        </div>
                      )}
                    </div>
                    <div className={`flex-1 relative h-full`} />
                  </>
                )}
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { KpiTrendChart };
